import { startAuthentication } from "@simplewebauthn/browser";

/**
 * The API client.
 *
 * One place that knows how a request is shaped, so a 401 always means the same
 * thing to the UI and no component has to remember to send credentials — and one
 * place that knows a step-up is not a sign-out.
 */

export class ApiError extends Error {
    constructor(message, status, body) {
        super(message);
        this.status = status;
        this.body = body;
    }
}

async function request(path, options = {}) {
    const { method = "GET", body, signal, headers = {}, retried = false } = options;
    const response = await fetch(`/api${path}`, {
        method,
        headers: { ...(body ? { "content-type": "application/json" } : {}), ...headers },
        body: body ? JSON.stringify(body) : undefined,
        credentials: "same-origin",
        signal
    });

    const text = await response.text();
    let parsed = null;
    try {
        parsed = text ? JSON.parse(text) : null;
    } catch {
        parsed = text;
    }

    if (!response.ok) {
        /*
         * Revealing env values, minting a token and deleting a project ask for the
         * passkey again however long this browser has been trusted. The session is
         * fine — so this is answered here and the call retried, rather than every
         * caller learning which routes ask.
         */
        if (response.status === 401 && parsed?.need === "step-up" && !retried) {
            await stepUp(parsed);
            return request(path, { ...options, retried: true });
        }
        throw new ApiError(parsed?.error ?? `request failed (${response.status})`, response.status, parsed);
    }
    return parsed;
}

/**
 * Two calls refused at once must not open two passkey prompts: WebAuthn allows
 * one outstanding request and the browser simply rejects the second. They share
 * the one confirmation instead.
 */
let confirming = null;

async function stepUp(refusal) {
    confirming ??= (async () => {
        const options = await request("/auth/step-up/options", { method: "POST" });
        const credential = await startAuthentication({ optionsJSON: options });
        await request("/auth/step-up/verify", { method: "POST", body: { credential } });
    })();
    try {
        await confirming;
    } catch {
        throw new ApiError("that needs your passkey — the confirmation was cancelled or failed", 401, refusal);
    } finally {
        confirming = null;
    }
}

export const api = {
    auth: {
        state: () => request("/auth/state"),
        me: () => request("/auth/me"),
        logout: () => request("/auth/logout", { method: "POST" }),
        registerOptions: (body) => request("/auth/passkey/register/options", { method: "POST", body }),
        registerVerify: (body) => request("/auth/passkey/register/verify", { method: "POST", body }),
        loginOptions: () => request("/auth/passkey/login/options", { method: "POST" }),
        loginVerify: (body) => request("/auth/passkey/login/verify", { method: "POST", body }),
        /** The one thing a trusted browser's cookie can do: mint a new session. */
        refresh: () => request("/auth/refresh", { method: "POST" }),
        devices: () => request("/auth/devices"),
        revokeDevice: (id) => request(`/auth/devices/${id}`, { method: "DELETE" }),
        revokeAllDevices: () => request("/auth/devices/revoke-all", { method: "POST" }),
        passkeys: () => request("/auth/passkeys"),
        renamePasskey: (id, label) => request(`/auth/passkeys/${id}`, { method: "PATCH", body: { label } }),
        removePasskey: (id) => request(`/auth/passkeys/${id}`, { method: "DELETE" })
    },
    /**
     * Working copies under ~/dev, and the Claude session on each.
     *
     * A project's slug is its directory name, so the project page and the
     * workspaces page call the same endpoints — there is no second mapping.
     */
    workspaces: {
        list: () => request("/workspaces"),
        get: (name) => request(`/workspaces/${name}`),
        clone: (name, repo) => request("/workspaces", { method: "POST", body: { name, repo } }),
        /** Past conversations for the working copy — running, stopped or on disk. */
        history: (name) => request(`/workspaces/${name}/sessions`),
        /** With `resume`, a previous conversation comes back instead of a blank one. */
        startSession: (name, resume = null) =>
            request(`/workspaces/${name}/session`, { method: "POST", body: resume ? { resume } : {} }),
        stopSession: (name) => request(`/workspaces/${name}/session`, { method: "DELETE" })
    },


    projects: {
        list: () => request("/projects"),
        get: (slug) => request(`/projects/${slug}`),
        schema: () => request("/projects/schema"),
        create: (body) => request("/projects", { method: "POST", body }),
        /** Repository, draft and working copy in one call. */
        start: (body) => request("/projects/new", { method: "POST", body }),
        /** A draft becomes a project: the hook is created and a push deploys it. */
        activate: (slug) => request(`/projects/${slug}/activate`, { method: "POST" }),
        update: (slug, body) => request(`/projects/${slug}`, { method: "PATCH", body }),
        remove: (slug) => request(`/projects/${slug}`, { method: "DELETE" }),
        deploy: (slug, body = {}) => request(`/projects/${slug}/deploy`, { method: "POST", body }),
        rollback: (slug, body = {}) => request(`/projects/${slug}/rollback`, { method: "POST", body }),
        /** Rewrite the env files and reload only the processes reading `groups`. */
        restart: (slug, groups) => request(`/projects/${slug}/restart`, { method: "POST", body: { groups } }),
        pause: (slug) => request(`/projects/${slug}/pause`, { method: "POST" }),
        resume: (slug) => request(`/projects/${slug}/resume`, { method: "POST" }),
        nginx: (slug) => request(`/projects/${slug}/nginx`),
        commits: (slug) => request(`/projects/${slug}/commits`),
        /** Validate a draft manifest and get back what a deploy would do with it. */
        validate: (slug, body) => request(`/projects/${slug}/validate`, { method: "POST", body }),
        webhook: (slug) => request(`/projects/${slug}/webhook`),
        configureWebhook: (slug, body = {}) => request(`/projects/${slug}/webhook`, { method: "POST", body })
    },

    env: {
        get: (slug, reveal = false) => request(`/projects/${slug}/env${reveal ? "?reveal=1" : ""}`),
        put: (slug, body) => request(`/projects/${slug}/env`, { method: "PUT", body }),
        revisions: (slug) => request(`/projects/${slug}/env/revisions`),
        revert: (slug, rev) => request(`/projects/${slug}/env/revert`, { method: "POST", body: { rev } })
    },

    deployments: {
        list: (query = {}) => request(`/deployments?${new URLSearchParams(query)}`),
        get: (id) => request(`/deployments/${id}`),
        cancel: (id) => request(`/deployments/${id}/cancel`, { method: "POST" }),
        /** The live log is an EventSource, not a fetch — see LogConsole. */
        streamUrl: (id) => `/api/deployments/${id}/stream`,
        logUrl: (id, format = "text") => `/api/deployments/${id}/log?format=${format}`
    },

    system: {
        health: () => request("/health"),
        info: () => request("/system"),
        history: () => request("/system/history"),
        pm2: () => request("/pm2"),
        pm2Action: (name, action) => request(`/pm2/${name}/${action}`, { method: "POST" }),
        pm2LogsUrl: (name, lines = 300) => `/api/pm2/${name}/logs?lines=${lines}`
    },

    notifications: {
        get: () => request("/notifications"),
        update: (body) => request("/notifications", { method: "PUT", body }),
        subscribe: (body) => request("/notifications/subscribe", { method: "POST", body }),
        removeDevice: (id) => request(`/notifications/devices/${id}`, { method: "DELETE" }),
        test: () => request("/notifications/test", { method: "POST" }),
        /** The count and the list in one request, so the bell can open instantly. */
        inbox: (limit = 30) => request(`/notifications/inbox?limit=${limit}`),
        /** No ids means all of them. */
        markRead: (ids) => request("/notifications/inbox/read", { method: "POST", body: ids ? { ids } : {} }),
        clearRead: () => request("/notifications/inbox", { method: "DELETE" })
    },

    /** Read-only lookups, so a form can offer choices instead of free text. */
    discover: {
        repos: (q = "") => request(`/gitlab/projects?q=${encodeURIComponent(q)}`),
        branches: (id) => request(`/gitlab/projects/${id}/branches`),
        zones: () => request("/dns/zones"),
        dns: (zone = "") => request(`/dns/records${zone ? `?zone=${zone}` : ""}`),
        nodeVersions: () => request("/node-versions"),
        paths: (path) => request(`/paths?path=${encodeURIComponent(path)}`)
    },

    infra: {
        ports: () => request("/ports"),
        domains: () => request("/domains"),
        /** Ask every domain whether it answers, now, rather than at the next pass. */
        checkDomains: () => request("/domains/check", { method: "POST" })
    },

    /** Deploys in flight and sessions alive: the two counts the sidebar hangs on its items. */
    activity: {
        get: () => request("/activity")
    },

    /** What changed, as it happens. An EventSource, not a fetch — see lib/events.js. */
    events: {
        url: () => "/api/events"
    },

    /** What is answering and what is not, from the monitor's last pass. */
    status: {
        get: () => request("/status"),
        /** One word for the nav dot; answered from memory, so it works while Mongo does not. */
        summary: () => request("/status/summary"),
        incidents: (query = {}) => request(`/status/incidents?${new URLSearchParams(query)}`),
        /** Ask everything now rather than waiting out the half minute. */
        check: () => request("/status/check", { method: "POST" })
    },

    /**
     * The live board, in one answer.
     *
     * A paired screen holds a key and no cookie, so the key travels as a bearer
     * header — the one place in this client that sets one. Without a key the
     * session cookie does the job, which is how a signed-in laptop previews it.
     */
    live: {
        get: (key) => request("/live", { headers: key ? { authorization: `Bearer ${key}` } : {} })
    },

    /** How the dashboard is set up: which layout the board shows. */
    preferences: {
        get: () => request("/preferences"),
        update: (body) => request("/preferences", { method: "PUT", body })
    },

    /** Targets somebody asked to be watched, as opposed to the ones the box discovers. */
    monitors: {
        list: () => request("/monitors"),
        create: (body) => request("/monitors", { method: "POST", body }),
        update: (id, body) => request(`/monitors/${id}`, { method: "PUT", body }),
        remove: (id) => request(`/monitors/${id}`, { method: "DELETE" })
    },

    tokens: {
        list: () => request("/tokens"),
        create: (body) => request("/tokens", { method: "POST", body }),
        revoke: (id) => request(`/tokens/${id}/revoke`, { method: "POST" }),
        // Only accepted once revoked; the server refuses otherwise.
        remove: (id) => request(`/tokens/${id}`, { method: "DELETE" })
    },

    /** Apps connected over MCP: the consent screen, the grants, and the changes waiting for a passkey. */
    oauth: {
        request: (id) => request(`/oauth/requests/${id}`),
        approve: (id, scopes) => request(`/oauth/requests/${id}/approve`, { method: "POST", body: { scopes } }),
        deny: (id) => request(`/oauth/requests/${id}/deny`, { method: "POST" }),
        grants: () => request("/oauth/grants"),
        disconnect: (id) => request(`/oauth/grants/${id}`, { method: "DELETE" }),
        approvals: () => request("/oauth/approvals"),
        approveChange: (id) => request(`/oauth/approvals/${id}/approve`, { method: "POST" }),
        denyChange: (id) => request(`/oauth/approvals/${id}/deny`, { method: "POST" })
    },

    audit: (query = {}) => request(`/audit?${new URLSearchParams(query)}`)
};
